/** @file Pointer state machine of the curve graph: pick, handle drag preview, box select, pan, scrub. */
import { HANDLE_PX } from '@pose2d/editor-geometry/handlePx';
import { normalizeRect } from '@pose2d/editor-geometry/normalizeRect';
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { CommandCall } from '@pose2d/format/types';
import type { KeyRef } from '../../stores/types';
import { CURVE_GRAPH } from './curveGraph.data';
import type { CurveHitGrid } from './curveHitGrid';
import { curveHitGrid } from './curveHitGrid';
import { CURVE_POINT } from './curvePoint.data';
import type { CurveActiveKey, CurveAxes, CurveGesture, CurveScene, CurveTrack, CurveTrackMap } from './curveTypes';
import { curveValueScale } from './curveValueScale';
import { dragCurveHandle } from './dragCurveHandle';
import { setCurveCall } from './setCurveCall';

/** What the last paint showed: document tracks (no preview), the painted scene and its grid, maps and axes. */
export interface CurvePointerContext extends CurveAxes {
  tracks: readonly CurveTrack[];
  scene: CurveScene;
  grid: CurveHitGrid;
  maps: readonly CurveTrackMap[];
}

/** One pointer event in canvas CSS pixels; Shift adds to the selection and locks a handle's time. */
export interface CurvePointerInput {
  x: number;
  y: number;
  button: number;
  shift: boolean;
  alt: boolean;
}

/** Side effects the view carries out (commit through the ports, selection, axes, playhead). */
export type CurvePointerEffect =
  | { kind: 'commit'; calls: CommandCall[] }
  | { kind: 'select'; keys: KeyRef[]; add: boolean; active: CurveActiveKey | null }
  | { kind: 'axes'; axes: CurveAxes }
  | { kind: 'scrub'; frame: number };

/** The next gesture and the effects of one event. */
export interface CurveStep {
  gesture: CurveGesture;
  effects: CurvePointerEffect[];
}

const IDLE: CurveStep = { gesture: { kind: 'idle' }, effects: [] };

/** The KeyRef of key `key` of scene track `track` (channel = the animation.setCurve channel name). */
function keyRef(ctx: CurvePointerContext, track: number, key: number): KeyRef {
  const t = ctx.scene.tracks[track];
  if (t === undefined) return { animation: '', target: '', channel: '', frame: 0 };
  return { animation: t.ref.animation, target: t.ref.target, channel: t.ref.channel, frame: t.frames[key] ?? 0 };
}

/** Key `key` of scene track `track` as the active key (its timeline and frame). */
function activeOf(ctx: CurvePointerContext, track: number, key: number): CurveActiveKey | null {
  const t = ctx.scene.tracks[track];
  return t === undefined ? null : { ref: t.ref, frame: t.frames[key] ?? 0 };
}

const scrub = (ctx: CurvePointerContext, x: number): CurvePointerEffect => ({ kind: 'scrub', frame: Math.max(0, timelineScale.snapFrame(ctx.time, x)) });

/** A key, or a curve sample (its segment's start key), selects that key; a handle starts a drag; else a box. */
function onPick(ctx: CurvePointerContext, hit: number, input: CurvePointerInput): CurveStep {
  const p = ctx.scene.points;
  const [kind, track, index] = [p.kind[hit] ?? -1, p.track[hit] ?? 0, p.index[hit] ?? 0];
  if (kind === CURVE_POINT.handle) return { gesture: { kind: 'handle', track, segment: index, which: p.which[hit] === 2 ? 2 : 1, curve: null }, effects: [] };
  if (kind === CURVE_POINT.key || kind === CURVE_POINT.sample) {
    return { gesture: { kind: 'idle' }, effects: [{ kind: 'select', keys: [keyRef(ctx, track, index)], add: input.shift, active: activeOf(ctx, track, index) }] };
  }
  return { gesture: { kind: 'box', x0: input.x, y0: input.y, x1: input.x, y1: input.y, add: input.shift }, effects: [] };
}

function dragTo(ctx: CurvePointerContext, g: Extract<CurveGesture, { kind: 'handle' }>, input: CurvePointerInput): CurveStep {
  const track = ctx.scene.tracks[g.track];
  if (track === undefined) return IDLE;
  const map = ctx.maps[g.track] ?? { offset: 0, gain: 1 };
  const frame = timelineScale.xToFrame(ctx.time, input.x);
  const value = curveValueScale.toValue(ctx.value, input.y) / map.gain + map.offset;
  return { gesture: { ...g, curve: dragCurveHandle(track, g, frame, value, input.shift) }, effects: [] };
}

/** Keys whose point lies in the box; a box under CURVE_GRAPH.boxMinPx on both sides is a click (clears unless adding). */
function boxKeys(ctx: CurvePointerContext, g: Extract<CurveGesture, { kind: 'box' }>): CurvePointerEffect {
  const r = normalizeRect(g);
  const p = ctx.scene.points;
  const keys = p.kind.flatMap((kind, i) => {
    const [x, y] = [p.x[i] ?? Number.NaN, p.y[i] ?? Number.NaN];
    const inside = kind === CURVE_POINT.key && x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
    return inside ? [keyRef(ctx, p.track[i] ?? 0, p.index[i] ?? 0)] : [];
  });
  return { kind: 'select', keys, add: g.add, active: null };
}

function release(ctx: CurvePointerContext, g: Extract<CurveGesture, { kind: 'handle' }>): CurveStep {
  const track = ctx.tracks[g.track];
  if (g.curve === null || track === undefined) return IDLE;
  if (JSON.stringify(track.curves[g.segment] ?? 'linear') === JSON.stringify(g.curve)) return IDLE;
  return { gesture: { kind: 'idle' }, effects: [{ kind: 'commit', calls: [setCurveCall(track.ref, [track.frames[g.segment] ?? 0], g.curve)] }] };
}

/** Event handlers; each returns the next gesture and the effects to carry out. */
export const curvePointer = {
  /** Middle button or Alt pans; the ruler strip scrubs; otherwise picks within HANDLE_PX.point. */
  down(ctx: CurvePointerContext, input: CurvePointerInput): CurveStep {
    if (input.button === 1 || (input.button === 0 && input.alt)) return { gesture: { kind: 'pan', x: input.x, y: input.y }, effects: [] };
    if (input.button !== 0) return IDLE;
    if (input.y < CURVE_GRAPH.rulerPx) return { gesture: { kind: 'scrub' }, effects: [scrub(ctx, input.x)] };
    return onPick(ctx, curveHitGrid.pick(ctx.grid, ctx.scene.points, input.x, input.y, HANDLE_PX.point), input);
  },
  /** Previews a handle drag, pans, grows the box or scrubs. */
  move(ctx: CurvePointerContext, g: CurveGesture, input: CurvePointerInput): CurveStep {
    switch (g.kind) {
      case 'idle':
        return IDLE;
      case 'handle':
        return dragTo(ctx, g, input);
      case 'pan': {
        const axes = { time: timelineScale.pan(ctx.time, input.x - g.x), value: curveValueScale.pan(ctx.value, input.y - g.y) };
        return { gesture: { kind: 'pan', x: input.x, y: input.y }, effects: [{ kind: 'axes', axes }] };
      }
      case 'box':
        return { gesture: { ...g, x1: input.x, y1: input.y }, effects: [] };
      case 'scrub':
        return { gesture: g, effects: [scrub(ctx, input.x)] };
    }
  },
  /** Commits a changed handle as one setCurve, or selects the boxed keys; every gesture ends. */
  up(ctx: CurvePointerContext, g: CurveGesture): CurveStep {
    if (g.kind === 'handle') return release(ctx, g);
    if (g.kind !== 'box') return IDLE;
    const small = Math.abs(g.x1 - g.x0) < CURVE_GRAPH.boxMinPx && Math.abs(g.y1 - g.y0) < CURVE_GRAPH.boxMinPx;
    if (small && g.add) return IDLE;
    return { gesture: { kind: 'idle' }, effects: [small ? { kind: 'select', keys: [], add: false, active: null } : boxKeys(ctx, g)] };
  },
};
